import React, { useMemo } from 'react';

import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { operations } from '@luxuryescapes/contract-svc-promo';

import DebugModal from '~/components/DebugModal/DebugModal';

import currencyFormatter from '~/utils/currencyFormatter';
import { getBookingNumber, getItemDescription } from '~/utils/order';

import PromoLink from '../PromoLink';
import { promoCodePath } from '../promoUrls';

import { SimulationWarning, buildSimulationRows } from './simulationResults';

export type SimulationResult =
  operations['calculateDiscountByOrderId']['responses']['200']['content']['application/json']['result'];

type Props = {
  order: App.Order | null;
  items: Record<string, App.AnyItem>;
  result: SimulationResult | null;
  codeName?: string;
  voucherIds: Array<string>;
  warnings: Array<SimulationWarning>;
};

export default function PromoSimulationResults({ order, items, result, codeName, voucherIds, warnings }: Props) {
  const { rows, warningRows } = useMemo(
    () => buildSimulationRows(order, items, result, warnings),
    [order, items, result, warnings],
  );
  const currency = result?.promo.currency || order?.currency_code;
  const amount = (value: number | null | undefined) =>
    value == null || !currency ? '-' : currencyFormatter(currency, value, 2);
  const columns: Array<GridColDef<(typeof rows)[number]>> = [
    { field: 'itemId', headerName: 'Item ID', minWidth: 180 },
    { field: 'description', headerName: 'Item', minWidth: 200, flex: 1 },
    { field: 'bookingNumber', headerName: 'Booking', minWidth: 130 },
    { field: 'itemType', headerName: 'Item type', minWidth: 140 },
    { field: 'category', headerName: 'Promo category', minWidth: 140 },
    ...[
      ['total', 'Order item price'],
      ['discountableTotal', 'Discountable price'],
      ['recordedPromo', 'Recorded promo'],
      ['simulatedPromo', 'Simulated promo'],
      ['recordedVoucher', 'Recorded vouchers'],
      ['simulatedVoucher', 'Simulated vouchers'],
      ['creditAmount', 'Credit reward'],
    ].map(
      ([field, headerName]): GridColDef => ({
        field,
        headerName,
        minWidth: 155,
        type: 'number',
        cellClassName: ({ row }) =>
          row.itemTotalMismatch && ['total', 'discountableTotal'].includes(field) ? 'item-price-mismatch' : '',
        valueFormatter: (value: number | undefined) => {
          const columnCurrency = ['total', 'recordedPromo', 'recordedVoucher'].includes(field)
            ? order?.currency_code || currency
            : currency;
          return value == null || !columnCurrency ? '-' : currencyFormatter(columnCurrency, value, 2);
        },
      }),
    ),
    {
      field: 'voucherBreakdown',
      headerName: 'Voucher allocations',
      minWidth: 280,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ py: 1, whiteSpace: 'pre-line' }}>
          {value || '-'}
        </Typography>
      ),
    },
    { field: 'discountRule', headerName: 'Discount rule', minWidth: 130 },
    { field: 'channelMarkupIds', headerName: 'Channel markup IDs', minWidth: 180 },
    {
      field: 'warnings',
      headerName: 'Warnings',
      minWidth: 300,
      flex: 2,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ py: 1, whiteSpace: 'pre-line' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => <DebugModal title="Item details" type="generic" data={row.details} />,
    },
  ];
  const warningColumns: Array<GridColDef> = [
    { field: 'scope', headerName: 'Level', width: 110 },
    { field: 'itemId', headerName: 'Item ID', minWidth: 180 },
    { field: 'title', headerName: 'Warning', minWidth: 220 },
    {
      field: 'message',
      headerName: 'Details',
      minWidth: 350,
      flex: 1,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ py: 1, whiteSpace: 'pre-line' }}>
          {value}
        </Typography>
      ),
    },
  ];
  const appliedVouchers = Array.from(
    new Map(
      result?.promo.items
        .flatMap((item) => item.vouchersApplied ?? [])
        .map((voucher) => [voucher.voucherId, voucher]) ?? [],
    ).values(),
  );

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Order
        </Typography>
        {order ? (
          <>
            <Typography variant="body2">Order ID: {order.id_orders || order.id}</Typography>
            <Typography variant="body2">Status: {order.status}</Typography>
            <Typography variant="body2">Total: {currencyFormatter(order.currency_code, order.total, 2)}</Typography>
            <Typography variant="body2">
              Currency: {order.currency_code} | Region: {order.region_code}
            </Typography>
            <Typography variant="body2">
              Recorded item promo total:{' '}
              {currencyFormatter(
                order.currency_code,
                Object.values(items).reduce((total, item) => total + (item.promo_code_discount_amount ?? 0), 0),
                2,
              )}
              {' | '}Promo payment total:{' '}
              {currencyFormatter(
                order.currency_code,
                (order.payments ?? [])
                  .filter((payment) => payment.type === 'promo')
                  .reduce((total, payment) => total + payment.total, 0),
                2,
              )}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button href={`/purchases/${order.id_orders || order.id}`} target="_blank" rel="noreferrer">
                View order
              </Button>
              <Button href={order.origin_url} target="_blank" rel="noreferrer">
                Restore cart
              </Button>
              <DebugModal title="Full order" type="generic" data={order} />
              <DebugModal title="Payments" type="generic" data={order.payments} />
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Order details unavailable.
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Promo and vouchers
        </Typography>
        {codeName && (
          <Typography variant="body2">
            Submitted promo:{' '}
            <Link href={promoCodePath(codeName)} target="_blank" rel="noreferrer">
              {codeName}
            </Link>
          </Typography>
        )}
        {voucherIds.map((id) => (
          <Typography key={id} variant="body2">
            Submitted voucher:{' '}
            <Link href={`/marketing/vouchers/${id}`} target="_blank" rel="noreferrer">
              {id}
            </Link>
          </Typography>
        ))}
        {result ? (
          <Stack spacing={1}>
            {result.promo.parent_id_promo_code && (
              <PromoLink promoId={result.promo.parent_id_promo_code} title="Parent promo" />
            )}
            {result.promo.code_name && <PromoLink codeName={result.promo.code_name} />}
            <Typography variant="body2">
              Calculated promo: {result.promo.code_name || '-'} | Promo discount: {amount(result.promo.discount_total)}{' '}
              | Vouchers: {amount(result.promo.voucherTotal)} | Credit rewards: {amount(result.promo.creditTotal)}
            </Typography>
            {appliedVouchers.map((voucher) => (
              <Stack key={voucher.voucherId} direction="row" spacing={1} alignItems="center">
                <Link href={`/marketing/vouchers/${voucher.voucherId}`} target="_blank" rel="noreferrer">
                  {voucher.voucherName || voucher.voucherTitle || voucher.voucherId}
                </Link>
                <DebugModal title="Voucher details" type="generic" data={voucher} />
              </Stack>
            ))}
            <Box>
              <DebugModal title="Calculation details and internals" type="generic" data={result} />
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No calculation result.
          </Typography>
        )}
      </Box>
      <Box>
        <Typography variant="h6" gutterBottom>
          Items ({rows.length})
        </Typography>
        <DataGrid
          autoHeight
          rows={rows.map((row) => ({
            ...row,
            description: row.details.orderItem ? getItemDescription(row.details.orderItem, row.itemType) : '-',
            bookingNumber: row.details.orderItem ? getBookingNumber(row.details.orderItem) : '-',
          }))}
          columns={columns}
          sx={{ '& .item-price-mismatch': { color: 'error.main', fontWeight: 'fontWeightBold' } }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                itemId: false,
                bookingNumber: false,
                channelMarkupIds: false,
                discountRule: false,
                creditAmount: false,
                voucherBreakdown: false,
              },
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </Box>
      <Box>
        <Typography variant="h6" gutterBottom>
          Warnings ({warningRows.length})
        </Typography>
        <DataGrid
          autoHeight
          rows={warningRows}
          columns={warningColumns}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          localeText={{ noRowsLabel: 'No calculation warnings or mismatches.' }}
        />
      </Box>
    </Stack>
  );
}
